import { Typography, ArrowUpIcon, ArrowDownIcon } from '@weareopenr/component-library';
import styles from './PriceRuleTooltip.module.scss';
import * as Popover from '@radix-ui/react-popover';
import { CalculationInputType, type CalculationStep, type Rule } from '@weareopenr/common/build/src/utilities/pricing';
import { AdjustmentType, RoundingType, RoundingMethod } from '@weareopenr/types';
import { forwardRef, ReactElement, RefObject } from 'react';
import { IPriceRule } from 'src/types/price.types';
import { formatAdjustmentValue } from 'src/helpers/formatPrice/formatPrice';

const formatRuleAdjustment = (rule: Rule) => {
  const formatMap = {
    [AdjustmentType.FIXED]: `£${rule.adjustment.value.toFixed(2)}`,
    [AdjustmentType.PERCENTAGE]: `${rule.adjustment.value}%`,
  };
  return formatMap[rule.adjustment.type];
};

const roundingTypeMap = {
  [RoundingType.NEAREST]: 'Round to nearest',
  [RoundingType.UP]: 'Round up to',
  [RoundingType.DOWN]: 'Round down to',
};
const roundingMethodMap = {
  [RoundingMethod.INTERVAL]: 'interval of',
  [RoundingMethod.SPECIFIC]: 'specific value of',
};

const formatRuleRounding = (rule: Rule) => {
  return `${roundingTypeMap[rule.rounding.type]} ${roundingMethodMap[rule.rounding.method]} ${rule.rounding.value}`;
};

const inputTypeMap = {
  [CalculationInputType.BASE]: 'Base Price',
  [CalculationInputType.ADJUSTED]: 'Adjusted Price',
  [CalculationInputType.PRICE_BAND]: 'Band Price',
  [CalculationInputType.CHANNEL]: 'Channel Price',
};

const roundingIconsMap = {
  [RoundingType.UP]: <ArrowUpIcon />,
  [RoundingType.DOWN]: <ArrowDownIcon />,
  [RoundingType.NEAREST]: (
    <span className={styles['rounding-arrows']}>
      <ArrowUpIcon />
      <ArrowDownIcon />
    </span>
  ),
};

const renderInheritanceRounding = (rounding: IPriceRule['rounding']) => {
  if (rounding.type === RoundingType.NONE) return 'No rounding';
  const valueText = rounding.method ? `${rounding.value} (${rounding.method.toLowerCase()})` : `${rounding.value}`;
  return (
    <span className={styles['rounding-value']}>
      {roundingIconsMap[rounding.type]}
      <strong>{valueText}</strong>
    </span>
  );
};

type Props =
  | { calculationSteps: CalculationStep[]; tableContainerRef: RefObject<HTMLElement | null>; parentRule?: never }
  | { parentRule: IPriceRule; calculationSteps?: never; tableContainerRef?: never };

export const PriceRuleToolTip = forwardRef<HTMLDivElement, Props>(
  ({ calculationSteps, tableContainerRef, parentRule }, ref): ReactElement => {
    if (parentRule) {
      const parentName =
        parentRule.pricing_category?.name ??
        parentRule.price_band?.name ??
        parentRule.extension_instance?.name ??
        'Unknown';
      const adjustmentText =
        parentRule.adjustment.type === AdjustmentType.NONE
          ? 'No adjustment'
          : formatAdjustmentValue(parentRule.adjustment);
      return (
        <Popover.Content ref={ref} className={styles['popover-content']} side="top">
          <Popover.Arrow />
          <Typography colour="secondary" size="xsmall">
            Inheriting rule from {parentName}
          </Typography>
          <Typography colour="secondary" size="xsmall">
            Calculation: <strong>{adjustmentText}</strong>
          </Typography>
          <Typography colour="secondary" size="xsmall">
            Rounding: {renderInheritanceRounding(parentRule.rounding)}
          </Typography>
        </Popover.Content>
      );
    }

    return (
      <Popover.Content
        ref={ref}
        className={styles['popover-content']}
        side="top"
        hideWhenDetached
        collisionBoundary={tableContainerRef.current}
        collisionPadding={{ top: 40 }}
      >
        <Popover.Arrow />
        {calculationSteps.map((step) => {
          return (
            <div key={step.stepNumber}>
              {calculationSteps.length > 1 && (
                <Typography colour="secondary" size="xsmall">
                  Step {step.stepNumber}:
                </Typography>
              )}
              <Typography colour="secondary" size="xsmall">
                {step.rule.price_band?.id && 'Price Band'}
                {step.rule.extension_instance?.id && 'Channel'}: £{step.input} ({inputTypeMap[step.inputType]})
                {step.rule.adjustment.type !== AdjustmentType.NONE && ` + ${formatRuleAdjustment(step.rule)}`}
              </Typography>
              {step.rule.rounding.type !== RoundingType.NONE && (
                <Typography colour="secondary" size="xsmall">
                  {formatRuleRounding(step.rule)}
                </Typography>
              )}
              <Typography colour="secondary" size="xsmall" weight="bold">
                {calculationSteps.length === step.stepNumber ? 'Final' : 'Adjusted'} price: £{step.output.toFixed(2)}
              </Typography>
            </div>
          );
        })}
      </Popover.Content>
    );
  },
);

PriceRuleToolTip.displayName = 'PriceRuleToolTip';
